import { MouseEvent, ReactNode, useCallback } from "react";
import { Box, Divider, Paper, Stack, Text, TextProps, UnstyledButton } from "@mantine/core";

export interface ContextMenuItemBase {
    key?: string;
    label?: string;
    labelProps?: Partial<TextProps>;
    leftSection?: ReactNode;
    divider?: boolean;
}

export interface ContextMenuItemItem extends ContextMenuItemBase {
    key: string;
    label: string;
    onClick?(event: MouseEvent<HTMLButtonElement>);
}

export interface ContextMenuItemDivider extends ContextMenuItemBase {
    divider: true;
}

export type ContextMenuItem = ContextMenuItemItem | ContextMenuItemDivider;

export interface ContextMenuProps {
    items: ContextMenuItem[];
    onClose();
}

export const ContextMenu = ({ items, onClose }: ContextMenuProps) => {
    const handleItemClick = useCallback((item: ContextMenuItemItem, event: MouseEvent<HTMLButtonElement>) => {
        item?.onClick?.(event);
        onClose?.();
    }, [onClose]);

    return (
        <Paper radius="sm" shadow="sm" withBorder>
            <Stack spacing="0">
                {items.map((item, index) => {
                    if (item?.divider) {
                        return <Divider color="gray.2" />;
                    }

                    return (
                        <UnstyledButton
                            key={`${index}${item?.key}`}
                            sx={(theme) => ({
                                padding: `calc(${theme.spacing.xs} / 1.5) ${theme.spacing.sm}`,
                                display: "flex",
                                alignItems: "center",
                                userSelect: "none",
                                backgroundColor: theme.colorScheme === "dark" ? theme.colors.dark[7] : theme.white,
                                borderTopLeftRadius: index === 0 ? theme.radius.sm : undefined,
                                borderTopRightRadius: index === 0 ? theme.radius.sm : undefined,
                                borderBottomLeftRadius: index + 1 >= items?.length ? theme.radius.sm : undefined,
                                borderBottomRightRadius: index + 1 >= items?.length ? theme.radius.sm : undefined,
                                "&:hover": {
                                    backgroundColor:
                                        theme.colorScheme === "dark" ? theme.colors.dark[6] : theme.colors.gray[0],
                                },
                            })}
                            role="menuitem"
                            onClick={(event) => handleItemClick(item as ContextMenuItemItem, event)}
                        >
                            {item?.leftSection && <Box mr="xs">{item.leftSection}</Box>}
                            <Text {...(item?.labelProps || {})} size="sm">
                                {item?.label}
                            </Text>
                        </UnstyledButton>
                    );
                })}
            </Stack>
        </Paper>
    );
};
